import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAppMode } from "@/hooks/useAppMode";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import BottomTabBar from "./BottomTabBar";
import { Loader2 } from "lucide-react";

interface MobileAppLayoutProps {
  children: ReactNode;
  title?: string;
  requireAuth?: boolean;
}

const MobileAppLayout = ({
  children,
  title,
  requireAuth = true,
}: MobileAppLayoutProps) => {
  const { provider, isAppMode, isLoading, slug } = useAppMode();
  const { user, loading } = useAuth();
  const location = useLocation();

  // Register device for push notifications (native only, no-op on web)
  usePushNotifications();

  if (isLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAppMode || !provider) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6 text-center">
        <div className="max-w-sm">
          <h1 className="text-xl font-semibold mb-2">App not configured</h1>
          <p className="text-sm text-muted-foreground">
            No provider is locked. Open with{" "}
            <code className="bg-muted px-1 rounded">?app=&lt;slug&gt;</code> to
            enter app mode.
          </p>
        </div>
      </div>
    );
  }

  if (requireAuth && !user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/app/auth?app=${slug}&next=${next}`} replace />;
  }

  const brandPrimary = provider.brand_primary_color || undefined;
  const displayName = provider.display_name || provider.name;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top branded bar */}
      <header
        className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="flex items-center gap-3 px-4 h-14">
          {provider.image_url ? (
            <img
              src={provider.image_url}
              alt={displayName}
              className="h-9 w-9 rounded-full object-cover"
            />
          ) : (
            <div
              className="h-9 w-9 rounded-full flex items-center justify-center text-white font-semibold"
              style={{ backgroundColor: brandPrimary || "hsl(var(--primary))" }}
            >
              {displayName.charAt(0)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate leading-tight">
              {displayName}
            </p>
            {title && (
              <p className="text-xs text-muted-foreground truncate leading-tight">
                {title}
              </p>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main
        className="flex-1 overflow-y-auto pb-24"
        style={{
          paddingLeft: "env(safe-area-inset-left)",
          paddingRight: "env(safe-area-inset-right)",
        }}
      >
        {children}
      </main>

      <BottomTabBar brandColor={brandPrimary} />
    </div>
  );
};

export default MobileAppLayout;
